/**
 * The shape of the docs' text, one object per language. Every list is keyed
 * by an id below, so a language that misses a page, a tip, a limit or a
 * question does not compile, and the same entry lines up in every language.
 *
 * Text is plain strings: `**…**` marks bold (Claude's menu names and buttons),
 * and `[[<page>]]` is a link to another page of the docs, shown as that
 * page's title (`[[connect-claude]]`). Tool titles and summaries are not
 * here: they come from the backend's catalogue through `toolText`, so the
 * docs never name a tool differently from the Tools page.
 */

import type { DocPageId } from "@/lib/docs-pages";

/** The example questions, in the order the page shows them. */
export const EXAMPLE_IDS = [
  "revenue",
  "overdue",
  "on-rent",
  "rentals",
  "deliveries",
  "map",
] as const;
export type ExampleId = (typeof EXAMPLE_IDS)[number];

/** The tips on how to ask, in order. */
export const TIP_IDS = ["period", "client-first", "say-what-you-mean", "form"] as const;
export type TipId = (typeof TIP_IDS)[number];

/** What the connector cannot do or see, in order. */
export const LIMIT_IDS = [
  "changes-nothing",
  "not-everything",
  "map-approximate",
  "closed-rental-site",
  "no-end-date",
  "occupancy-per-type",
  "finance-checks",
  "outside-odoo",
  "left-out",
  "answer-size",
] as const;
export type LimitId = (typeof LIMIT_IDS)[number];

/** What happens to the data, in order. */
export const PRIVACY_IDS = [
  "own-account",
  "same-for-everyone",
  "passes-through",
  "log-metadata",
] as const;
export type PrivacyId = (typeof PRIVACY_IDS)[number];

/**
 * The rows of the table of who sees what, on the privacy page, in order:
 * what everyone gets, what the admin roles add (the admin pages, the notes
 * on the connector's issues and the roadmap's progress), then the roadmap's
 * budget for an owner and a developer, and the roadmap's work, the issues'
 * status, pausing a tool for everyone and viewing the dashboard as someone
 * else for a developer. Which role has each row is not text: the docs' code
 * reads it from the roles (`features/docs/role-access.ts`).
 */
export const ROLE_ACCESS_IDS = [
  "connector",
  "start-and-docs",
  "apps",
  "own-switches",
  "admin-pages",
  "issue-notes",
  "roadmap",
  "roadmap-budget",
  "roadmap-work",
  "issue-work",
  "pause-tools",
  "view-as",
] as const;
export type RoleAccessId = (typeof ROLE_ACCESS_IDS)[number];

/** The questions of the FAQ, in order. */
export const FAQ_IDS = [
  "not-listed",
  "sign-in-fails",
  "not-from-odoo",
  "cut-short",
  "slow",
  "number-wrong",
  "how-looked-up",
] as const;
export type FaqId = (typeof FAQ_IDS)[number];

/** A point in a list of the docs: a short lead in bold, then the explanation. */
export interface DocEntry {
  /** The lead, without a full stop: the list adds it. */
  title: string;
  body: string;
}

/** Paragraphs in order; a list of strings is a bulleted list. */
export type DocBlocks = readonly (string | readonly string[])[];

/** A question of the FAQ and its answer. */
export interface FaqEntry {
  question: string;
  answer: DocBlocks;
}

export interface DocsContent {
  page: {
    /** The docs' `<h1>`, on every page; keep it the same as the side navigation's item. */
    title: string;
    /** The line under the title. */
    description: string;
    /** The accessible name of the docs' navigation, the list of pages. */
    contentsLabel: string;
    /** The accessible name of the links to the previous and the next page. */
    pagerLabel: string;
    /** Above the link to the previous page's title. */
    previous: string;
    /** Above the link to the next page's title. */
    next: string;
  };

  /** Each page's title: its heading, its link in the navigation, and its name in links to it. */
  pages: Record<DocPageId, string>;

  connect: {
    /** The intro on an admin's page, which tells the steps in their order: the Owner's, then everyone's. */
    intro: string;
    /**
     * The intro on a colleague's page, which starts with their own steps and
     * points to the Owner's, folded away at the end.
     */
    introEveryoneFirst: string;
    /** A note above the steps while the connector is off. */
    modeOff: string;
    /** A note above the steps when the connector runs in local test mode. */
    modeLocal: string;
    ownerHeading: string;
    /**
     * The connector's URL follows the second step, which ends where it
     * belongs ("paste this URL:"); the third step comes after the URL.
     */
    ownerSteps: readonly [open: string, name: string, allow: string];
    /**
     * The title a colleague clicks to open the Owner's steps, folded away at
     * the end of their page (an admin's page shows them first, under
     * `ownerHeading`).
     */
    ownerFolded: string;
    /** The heading of everyone's steps after the Owner's, on an admin's page. */
    everyoneHeading: string;
    /**
     * The heading of everyone's steps when they come first, on a colleague's
     * page: without the "then" that follows the Owner's steps.
     */
    everyoneFirstHeading: string;
    everyoneSteps: readonly [
      open: string,
      connect: string,
      signIn: string,
      allow: string,
      test: string,
    ];
    accessNote: string;
    urlLoading: string;
    /** Instead of the URL while the connector is off. */
    urlNotOnYet: string;
    /** Instead of the URL when the overview could not be loaded. */
    urlFailed: string;
  };

  /** The copy button next to the connector's URL. */
  copyUrl: {
    button: string;
    /** The button's text for a moment after a copy. */
    copied: string;
    /** The button's accessible name. */
    label: string;
    /** Read out by screen readers after a copy. */
    copiedStatus: string;
    failed: string;
  };

  capabilities: {
    /** Opens the page on what to ask. */
    intro: string;
    /** Opens the page of the tools, above the link to the Tools page. */
    toolsNote: string;
    /** The link to the Tools page. */
    toolsLink: string;
    /** Instead of the list while the catalogue loads. */
    toolsLoading: string;
    /** Instead of the list when the catalogue could not be loaded. */
    toolsFailed: string;
    /** Between a tool's title (in bold) and its summary. */
    toolSeparator: string;
    examplesHeading: string;
    /** The quotation marks around each example question. */
    quotes: readonly [open: string, close: string];
    examples: Record<ExampleId, string>;
    /** Explains the client placeholder in the examples. */
    examplesNote: string;
    tipsHeading: string;
    tips: Record<TipId, DocEntry>;
  };

  limits: Record<LimitId, DocEntry>;
  privacy: Record<PrivacyId, DocEntry>;

  /**
   * Who sees what on the dashboard, per role: a table on the privacy page
   * with a column per role, named as the Mensen tab names it (`roleLabel`).
   */
  roles: {
    heading: string;
    /** Above the table: one role each, apart from the role in the Claude organization. */
    intro: string;
    /** The heading of the table's first column, over what each row gives. */
    access: string;
    /** Read out for a cell with a check mark: the role has it. */
    yes: string;
    /** Read out for an empty cell: the role does not have it. */
    no: string;
    rows: Record<RoleAccessId, string>;
    /** Under the table: the admin roles are in the Admins team, with every tool and app. */
    note: string;
  };
  faq: Record<FaqId, FaqEntry>;
}
